/* ============================================
   NODUS HEADER
   Mobile drawer toggle for snippets/nodus-header.liquid
   ============================================ */

if (!customElements.get('nodus-header')) {
	class NodusHeader extends HTMLElement {
		connectedCallback() {
			this.setupScroll();

			this.drawer = null;
			this.openButton = this.querySelector('[data-nodus-header-open]');

			if (!this.openButton) return;

			const drawerId = this.openButton.getAttribute('aria-controls');
			this.drawer = drawerId ? document.getElementById(drawerId) : null;

			if (!this.drawer) return;

			this.handleOpen = this.open.bind(this);
			this.handleDrawerClick = this.onDrawerClick.bind(this);
			this.handleKeydown = this.onKeydown.bind(this);

			this.openButton.addEventListener('click', this.handleOpen);
			this.drawer.addEventListener('click', this.handleDrawerClick);
			document.addEventListener('keydown', this.handleKeydown);
		}

		setupScroll() {
			this.headerGroup = this.closest('.nodus-header__group');

			if (!this.headerGroup) return;

			this.transparentGroup = this.headerGroup.classList.contains('nodus-header__group--transparent')
				? this.headerGroup
				: null;
			this.hideOn = this.headerGroup.dataset.nodusHeaderHideOn || '';
			this.autoHide = this.hideOn === 'down' || this.hideOn === 'up';

			if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
				this.autoHide = false;
			}

			if (!this.autoHide && !this.transparentGroup) return;

			this.scrollOffset = Number(this.headerGroup.dataset.nodusHeaderOffset) || 40;
			this.lastScrollY = window.scrollY;
			this.scrollFrame = null;
			this.handleScroll = this.onScroll.bind(this);

			window.addEventListener('scroll', this.handleScroll, { passive: true });
			this.syncHeaderState();
		}

		onScroll() {
			if (this.scrollFrame) return;

			this.scrollFrame = window.requestAnimationFrame(() => {
				this.scrollFrame = null;
				this.syncHeaderState();
			});
		}

		syncHeaderState() {
			const y = Math.max(0, window.scrollY);

			if (this.transparentGroup) {
				this.transparentGroup.classList.toggle('is-scrolled', y > this.scrollOffset);
			}

			if (this.autoHide) {
				const drawerOpen = document.body.classList.contains('nodus-header-drawer-open');
				const delta = y - this.lastScrollY;

				if (drawerOpen || y <= this.scrollOffset) {
					this.headerGroup.classList.remove('is-hidden');
				} else if (Math.abs(delta) > 4) {
					const hide = this.hideOn === 'down' ? delta > 0 : delta < 0;
					this.headerGroup.classList.toggle('is-hidden', hide);
				}
			}

			this.lastScrollY = y;
		}

		disconnectedCallback() {
			if (this.headerGroup && this.handleScroll) {
				window.removeEventListener('scroll', this.handleScroll);
			}

			if (this.scrollFrame) {
				window.cancelAnimationFrame(this.scrollFrame);
				this.scrollFrame = null;
			}

			if (this.openButton && this.handleOpen) {
				this.openButton.removeEventListener('click', this.handleOpen);
			}

			if (this.drawer && this.handleDrawerClick) {
				this.drawer.removeEventListener('click', this.handleDrawerClick);
			}

			if (this.handleKeydown) {
				document.removeEventListener('keydown', this.handleKeydown);
			}

			document.body.classList.remove('nodus-header-drawer-open');
		}

		open() {
			this.drawer.hidden = false;
			this.openButton.setAttribute('aria-expanded', 'true');
			document.body.classList.add('nodus-header-drawer-open');
			this.headerGroup?.classList.remove('is-hidden');

			const firstLink = this.drawer.querySelector('a, button');

			if (firstLink) firstLink.focus();
		}

		close() {
			this.drawer.hidden = true;
			this.openButton.setAttribute('aria-expanded', 'false');
			document.body.classList.remove('nodus-header-drawer-open');
			this.openButton.focus();
		}

		onDrawerClick(event) {
			if (event.target.closest('[data-nodus-header-close]') || event.target.closest('a')) {
				this.close();
			}
		}

		onKeydown(event) {
			if (event.key === 'Escape' && !this.drawer.hidden) this.close();
		}
	}

	customElements.define('nodus-header', NodusHeader);
}
